/**
 * today-digest-derive.ts — Pure derive helpers for the Today "needs attention" digest banner (A3).
 * Combines concern flags from multiple derives into a single DigestState.
 * No side effects; all functions are deterministic given inputs.
 */
import type { InboxItem, WeeklyReview } from "../types";
import type { LifeOsStateWithRecurring } from "../lib/life-os-contract";
import { isOverCapacity, plannedMinutes, todaysPlan } from "./today-rituals-derive";
import { isWeeklyReviewDue } from "./review-derive";
import { isInboxStale, waitingOverdue } from "./inbox-derive";
import { deriveToday } from "./bento-derive";
import { monthlyCloseStatus } from "./finance-monthly-close-derive";
import { getDayCapacityMinutes, getMonthlyCloseDay } from "../lib/app-prefs";
import { todayIso } from "../lib/date";

export interface DigestState {
  /** Number of overdue open tasks (d.overdue.length). */
  overdueCount: number;
  /** True when planned MIT minutes exceed day capacity. */
  overCapacity: boolean;
  /** True when a weekly review is due (>7 days since last). */
  reviewDue: boolean;
  /** True when any inbox-status items are older than 3 days. */
  inboxStale: boolean;
  /** Number of waiting inbox items whose follow-up date has passed. */
  waitingOverdueCount: number;
  /** The overdue waiting items (for display or linking). */
  waitingOverdueItems: InboxItem[];
  /** D47: tháng vừa khép chưa chốt (đã tới ngày chốt) → nhắc chốt tháng. */
  monthClosePending: boolean;
  /** Tháng cần chốt (YYYY-MM) — chỉ ý nghĩa khi monthClosePending. */
  monthCloseTarget: string;
  /** D49: số ngân sách đã sắp chạm ngưỡng cảnh báo (chưa vượt hẳn). */
  budgetAlertCount: number;
}

/** True when all concerns are clear — no action needed. */
export function isAllClear(d: DigestState): boolean {
  return !d.overdueCount && !d.overCapacity && !d.reviewDue && !d.inboxStale && !d.waitingOverdueCount && !d.monthClosePending && !d.budgetAlertCount;
}

/**
 * Return inbox items whose waitingUntil date is on or before today.
 * Reuses waitingOverdue from inbox-derive (DRY).
 */
export function deriveWaitingOverdue(inboxItems: InboxItem[], today: string): InboxItem[] {
  return inboxItems.filter((item) => waitingOverdue(item, today));
}

/**
 * Compute digest state from live LifeOS state.
 * `today` defaults to todayIso() — pass explicitly in tests.
 */
export function deriveDigest(
  state: LifeOsStateWithRecurring,
  today = todayIso(),
): DigestState {
  const d = deriveToday(state);
  const plan = todaysPlan(state, today);
  const capacity = getDayCapacityMinutes();
  const planned = plannedMinutes(plan?.mitTaskIds ?? [], state.tasks);
  const waitingItems = deriveWaitingOverdue(state.inbox, today);
  const close = monthlyCloseStatus(state, today, getMonthlyCloseDay());

  return {
    overdueCount: d.overdue.length,
    overCapacity: isOverCapacity(planned, capacity),
    reviewDue: isWeeklyReviewDue(state.weeklyReviews, today),
    inboxStale: isInboxStale(state.inbox, today),
    waitingOverdueCount: waitingItems.length,
    waitingOverdueItems: waitingItems,
    monthClosePending: close.pending,
    monthCloseTarget: close.targetMonth,
    budgetAlertCount: d.budgetAlerts.length,
  };
}

/**
 * Lower-level variant for composing from already-computed values.
 * Avoids re-running derives when today-view already has them in scope.
 */
export function buildDigest({
  overdueCount,
  overCapacity,
  reviewDue,
  inboxItems,
  today,
  monthClosePending = false,
  monthCloseTarget = "",
  budgetAlertCount = 0,
}: {
  overdueCount: number;
  overCapacity: boolean;
  reviewDue: boolean;
  inboxItems: InboxItem[];
  weeklyReviews?: WeeklyReview[];
  today: string;
  monthClosePending?: boolean;
  monthCloseTarget?: string;
  budgetAlertCount?: number;
}): DigestState {
  const waitingItems = deriveWaitingOverdue(inboxItems, today);
  return {
    overdueCount,
    overCapacity,
    reviewDue,
    inboxStale: isInboxStale(inboxItems, today),
    waitingOverdueCount: waitingItems.length,
    waitingOverdueItems: waitingItems,
    monthClosePending,
    monthCloseTarget,
    budgetAlertCount,
  };
}
